import { Typography } from '@bowtie-ins/bowkit';
import {
  FemaleActive,
  FemaleInactive,
  MaleActive,
  MaleInactive,
  NonSmokerActive,
  NonSmokerInactive,
  SmokerActive,
  SmokerInactive,
} from '@bowtie-ins/bowkit/images';
import { ChangeEvent, ReactNode, useCallback, useMemo } from 'react';
import {
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  OutlinedInput,
} from '@material-ui/core';
import { makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import { DemographicInput } from '../../types';
import { range } from 'lodash';
import { QuotationContextValue } from '../../contexts/QuotationContext';
import { FEMALE, MALE, NON_SMOKER, SMOKER } from '../../constants';
import { EXTRA_PRODUCT_SPECS_BY_CODE } from '../../constants/product';

type DemographicInputFieldsProps = Pick<
  QuotationContextValue,
  'activeProduct' | 'setActiveProduct' | 'rawProducts'
> & {
  formValues: QuotationContextValue['values'];
  onFormValuesChange: QuotationContextValue['handleChange'];
  demographicInputs: DemographicInput[];
  onValueChange: (
    event: ChangeEvent<{ name?: string; value: unknown }>,
  ) => void;
  onVariableAttributeChange: (params: Record<string, string>) => void;
};

const useStyles = makeStyles(theme => ({
  fieldContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    [theme.breakpoints.down('xs')]: {
      justifyContent: 'space-between',
    },
    [theme.breakpoints.up('sm')]: {
      justifyContent: 'center',
    },
  },
}));

export const DemographicInputFields = ({
  activeProduct,
  setActiveProduct,
  rawProducts,
  demographicInputs,
  formValues,
  onFormValuesChange,
  onValueChange,
  onVariableAttributeChange,
}: DemographicInputFieldsProps) => {
  const intl = useIntl();
  const { fieldContainer } = useStyles();

  const theme = useTheme();
  const isXS = useMediaQuery(theme.breakpoints.down('xs'));

  const iconSize = useMemo(() => {
    return isXS ? 32 : 44;
  }, [isXS]);

  const isBbOrJuvenileCriticalIllnessProduct = useMemo(
    () => ['bb-ci', 'juvenile-ci'].includes(activeProduct?.code || ''),
    [activeProduct?.code],
  );

  const disabled = useMemo(() => !activeProduct, [activeProduct]);

  const genderOptions = useMemo(() => {
    return [
      {
        value: MALE,
        activeIcon: <MaleActive width={iconSize} height={iconSize} />,
        inactiveIcon: <MaleInactive width={iconSize} height={iconSize} />,
      },
      {
        value: FEMALE,
        activeIcon: <FemaleActive width={iconSize} height={iconSize} />,
        inactiveIcon: <FemaleInactive width={iconSize} height={iconSize} />,
      },
    ];
  }, [iconSize]);

  const smokerOptions = useMemo(() => {
    return [
      {
        value: NON_SMOKER,
        activeIcon: <NonSmokerActive width={iconSize} height={iconSize} />,
        inactiveIcon: <NonSmokerInactive width={iconSize} height={iconSize} />,
      },
      {
        value: SMOKER,
        activeIcon: <SmokerActive width={iconSize} height={iconSize} />,
        inactiveIcon: <SmokerInactive width={iconSize} height={iconSize} />,
      },
    ];
  }, [iconSize]);

  const ageOptions = useMemo(() => {
    if (isBbOrJuvenileCriticalIllnessProduct) {
      return [
        {
          label: intl.formatMessage({ id: 'age.option_label.unborn' }),
          value: 20,
        },
        {
          label: intl.formatMessage({ id: 'age.option_label.0' }),
          value: 0,
        },
        ...range(1, 18).map(value => ({ label: value, value })),
      ];
    }

    const min = Math.min(
      activeProduct
        ? activeProduct.min_issue_age_unit === 'days'
          ? Math.floor(activeProduct.min_issue_age_value / 365)
          : activeProduct.min_issue_age_value
        : 0,
    );
    const max = Math.max(activeProduct?.max_issue_age_value || 0);
    const baseValueOptions = range(
      min !== Infinity ? min : 0,
      max !== -Infinity ? max + 1 : 99,
    ).map(value => ({ label: value, value }));

    return baseValueOptions;
  }, [activeProduct, intl, isBbOrJuvenileCriticalIllnessProduct]);

  const isAgeValid = ageOptions
    .map(({ value }) => value)
    .includes(formValues.age);

  const handleAgeChange = useCallback(
    (
      event: ChangeEvent<{
        name?: string;
        value: unknown;
      }>,
    ) => {
      if (!isBbOrJuvenileCriticalIllnessProduct) {
        onValueChange(event);
        return;
      }

      const newProductCode =
        (event.target.value as number) >= 18 ? 'bb-ci' : 'juvenile-ci';

      const newProductIndex = rawProducts.findIndex(
        ({ code }) => code === newProductCode,
      );

      if (newProductIndex) {
        const defaultForm =
          EXTRA_PRODUCT_SPECS_BY_CODE[newProductCode].defaultFormValues || {};

        onVariableAttributeChange({
          productsShow: `${newProductIndex}`,
        });
        setActiveProduct(rawProducts[newProductIndex]);
        onFormValuesChange({
          ...defaultForm,
          age: event.target.value as string,
        });
      }
    },
    [
      isBbOrJuvenileCriticalIllnessProduct,
      rawProducts,
      onValueChange,
      onVariableAttributeChange,
      setActiveProduct,
      onFormValuesChange,
    ],
  );

  const shouldShowGender = demographicInputs.includes(DemographicInput.Gender);
  const shouldShowSmoker = demographicInputs.includes(DemographicInput.Smoker);

  const genderField = shouldShowGender ? (
    <Box className={fieldContainer} key="gender">
      <Typography variant="body-l">
        <FormattedMessage
          id={isBbOrJuvenileCriticalIllnessProduct ? 'gender.child' : 'gender'}
        />
      </Typography>
      <RadioGroup
        name="gender"
        value={formValues.gender}
        onChange={onValueChange}
        row
        style={{ flexWrap: 'nowrap' }}
      >
        {genderOptions.map(
          ({ value, activeIcon: ActiveIcon, inactiveIcon: InactiveIcon }) => (
            <FormControlLabel
              style={isXS ? { margin: 0 } : undefined}
              label={null}
              key={value}
              value={value}
              disabled={disabled}
              control={
                <Radio
                  style={{ padding: 4 }}
                  disableRipple
                  checkedIcon={ActiveIcon}
                  icon={InactiveIcon}
                  data-ui-component="Select"
                  data-ui-variant="Sex"
                />
              }
            />
          ),
        )}
      </RadioGroup>
    </Box>
  ) : null;

  const ageField = (
    <Box
      key="age"
      className={fieldContainer}
      style={isXS ? { paddingRight: 0 } : undefined}
    >
      <Typography variant="body-l">
        <FormattedMessage id="age" />
      </Typography>
      <Select
        native
        variant="outlined"
        name="age"
        value={formValues.age}
        onChange={handleAgeChange}
        input={
          <OutlinedInput
            labelWidth={0}
            style={
              isXS
                ? { margin: 0 }
                : {
                    marginTop: 6,
                    marginBottom: 6,
                    padding: 2,
                    marginRight: 16,
                  }
            }
          />
        }
        renderValue={value => (
          <Typography variant="body-m">{value as string}</Typography>
        )}
        disabled={disabled}
        data-ui-component="Select"
        data-ui-variant="Age"
      >
        {!isAgeValid && (
          <option key={undefined} value={undefined}>
            {formValues.age}
          </option>
        )}
        {ageOptions.map(({ value, label }) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </Select>
    </Box>
  );

  const smokerField = shouldShowSmoker ? (
    <Box className={fieldContainer} key="smoker">
      <Typography variant="body-l">
        <FormattedMessage id="smokingStatus" />
      </Typography>
      <RadioGroup
        name="smoker"
        value={formValues.smoker}
        onChange={onValueChange}
        row
        style={{ flexWrap: 'nowrap' }}
      >
        {smokerOptions.map(
          ({ value, activeIcon: ActiveIcon, inactiveIcon: InactiveIcon }) => (
            <FormControlLabel
              style={isXS ? { margin: 0 } : undefined}
              label={null}
              key={value}
              value={value}
              disabled={disabled}
              control={
                <Radio
                  style={{ padding: 4 }}
                  disableRipple
                  checkedIcon={ActiveIcon}
                  icon={InactiveIcon}
                  data-ui-component="Select"
                  data-ui-variant="Smoker"
                />
              }
            />
          ),
        )}
      </RadioGroup>
    </Box>
  ) : null;

  const mappings: Record<DemographicInput, ReactNode> = {
    [DemographicInput.Age]: ageField,
    [DemographicInput.Gender]: genderField,
    [DemographicInput.Smoker]: smokerField,
  };

  return (
    <Box
      display="grid"
      gridGap={isXS ? 0 : 8}
      gridTemplateColumns={`repeat(${demographicInputs.length}, 1fr)`}
      alignItems="stretch"
      style={isXS ? { height: 72 } : { height: 96 }}
      px={isXS ? 0 : 1}
    >
      {demographicInputs.map(input => mappings[input])}
    </Box>
  );
};
